import { closedAtPrintedZero } from '../model/accountMonths'
import { statementsSharingAMonth } from '../model/accountTimelines'
import {
  accountFilterOf,
  moneyMovementCount,
  placementDate,
  totalActivity,
} from '../model/activityView'
import { consecutiveRuns, monthOf, monthsBetween } from '../model/months'
import {
  isReadWithoutAccount,
  type NotCounted,
  type PortfolioOverview,
  type SeriesPoint,
  statementCashReasons,
} from '../model/portfolioOverview'
import { isFailedRead } from '../model/statementReasons'
import type {
  AccountStatement,
  AccountTimeline,
  ActivityEntry,
  BadgeReason,
  DateWindow,
  MonthKey,
  VerifyingStatement,
} from '../model/types'

export interface StatementMoney {
  netMoney: number
  // Deposits, withdrawals, transfers and unsorted ledger rows counted. Zero means "None found".
  movementCount: number
}

export type HistoryRow =
  | {
      kind: 'statement'
      statement: AccountStatement
      // Null when there is nothing earlier to compare with.
      change: number | null
      // Set when change is null: the month that is missing, or null for the first statement.
      missingBefore: MonthKey | null
      money: StatementMoney
      // The statement's own reasons, then the cash reason the account's badge gives it.
      reasons: BadgeReason[]
    }
  | { kind: 'verifying'; statement: VerifyingStatement }
  // A run of months next to each other with no statement, oldest first.
  | { kind: 'missing'; months: MonthKey[] }

const coveredMonths = (timeline: AccountTimeline) =>
  new Set([
    ...timeline.statements.flatMap((statement) => statement.coveredMonths),
    ...timeline.verifying.flatMap((statement) => statement.coveredMonths),
  ])

// Months with no statement between the first one and the As-of month. An account whose last
// statement printed a $0.00 total has ended, so the months after it are not gaps.
export const missingMonths = (
  timeline: AccountTimeline,
  month: MonthKey,
): MonthKey[] => {
  const statements = timeline.statements.filter(
    (statement) => statement.month <= month,
  )
  const first = statements[0]
  if (!first) return []
  const last = statements[statements.length - 1]
  const until = closedAtPrintedZero(last) ? last.month : month
  const covered = coveredMonths(timeline)
  return monthsBetween(first.month, until).filter(
    (candidate) => !covered.has(candidate),
  )
}

const moneyOf = (entries: ActivityEntry[], accountId: string) => {
  const totals = totalActivity(entries, accountFilterOf([accountId]))
  return {
    netMoney: totals.netMoney,
    movementCount: moneyMovementCount(totals),
  }
}

const moneyByOverview = new WeakMap<
  PortfolioOverview,
  Map<string, Map<number, StatementMoney>>
>()

// Money in and out inside each statement's own dates. Built once per account.
const statementMoney = (
  overview: PortfolioOverview,
  timeline: AccountTimeline,
): Map<number, StatementMoney> => {
  const byAccount = moneyByOverview.get(overview) ?? new Map()
  moneyByOverview.set(overview, byAccount)
  const cached = byAccount.get(timeline.id)
  if (cached) return cached
  const entries = overview.activity.filter(
    (entry) => entry.accountId === timeline.id,
  )
  const money = new Map(
    timeline.statements.map((statement) => {
      const from = statement.startDate ?? `${statement.month}-01`
      const inStatement = entries.filter((entry) => {
        const date = placementDate(entry)
        return date >= from && date <= statement.endDate
      })
      return [statement.segmentId, moneyOf(inStatement, timeline.id)]
    }),
  )
  byAccount.set(timeline.id, money)
  return money
}

const statementRow = (
  statement: AccountStatement,
  previous: AccountStatement | undefined,
  missing: Set<MonthKey>,
  money: StatementMoney,
  cashReasons: BadgeReason[],
): HistoryRow => {
  const firstCovered = statement.coveredMonths[0] ?? statement.month
  const gapBefore = Array.from(missing)
    .filter((month) => month < firstCovered)
    .filter((month) => !previous || month > previous.month)
    .pop()
  const canCompare =
    previous?.value != null && statement.value != null && !gapBefore
  return {
    kind: 'statement',
    statement,
    change: canCompare
      ? Math.round(((statement.value ?? 0) - (previous?.value ?? 0)) * 100) /
        100
      : null,
    missingBefore: gapBefore ?? null,
    money,
    reasons: [...statement.reasons, ...cashReasons],
  }
}

const NO_MONEY: StatementMoney = { netMoney: 0, movementCount: 0 }

// One row per statement up to the As-of month, one per run of missing months and one per statement
// still being verified. Newest first.
export const accountHistory = (
  overview: PortfolioOverview,
  timeline: AccountTimeline,
  month: MonthKey,
): HistoryRow[] => {
  const statements = timeline.statements.filter(
    (statement) => statement.month <= month,
  )
  const gaps = missingMonths(timeline, month)
  const missing = new Set(gaps)
  const money = statementMoney(overview, timeline)
  const rows: { month: MonthKey; row: HistoryRow }[] = [
    ...statements.map((statement, position) => ({
      month: statement.month,
      row: statementRow(
        statement,
        statements[position - 1],
        missing,
        money.get(statement.segmentId) ?? NO_MONEY,
        statementCashReasons(overview, timeline.id, statement),
      ),
    })),
    ...consecutiveRuns(gaps).map((run) => ({
      month: run[run.length - 1],
      row: { kind: 'missing', months: run } as HistoryRow,
    })),
    ...timeline.verifying
      .filter((statement) => statement.month <= month)
      .map((statement) => ({
        month: statement.month,
        row: { kind: 'verifying', statement } as HistoryRow,
      })),
  ]
  return rows
    .sort((first, second) => second.month.localeCompare(first.month))
    .map(({ row }) => row)
}

const lastDayOf = (month: MonthKey) => {
  const [year, monthNumber] = month.split('-').map(Number)
  const day = new Date(year, monthNumber, 0).getDate()
  return `${month}-${String(day).padStart(2, '0')}`
}

const NO_SERIES_MONEY = { moneyIn: 0, moneyOut: 0, netMoney: 0 }

const notCountedOf = (
  timeline: AccountTimeline,
  months: MonthKey[],
  reason: NotCounted['reason'] = 'no_statement',
): NotCounted[] =>
  months.map((gap) => ({
    accountId: timeline.id,
    accountName: timeline.name,
    month: gap,
    reason,
  }))

const failedReadMonths = (timeline: AccountTimeline, month: MonthKey) =>
  timeline.statements
    .filter((statement) => statement.month <= month && isFailedRead(statement))
    .map((statement) => statement.month)

// A point at each statement month, plus the As-of month when it has no statement. The missing
// months up to a point ride on it as not counted, so the chart folds a run into one hatched band,
// the same way the All accounts series does.
export const accountSeries = (
  timeline: AccountTimeline,
  month: MonthKey,
): SeriesPoint[] => {
  const statements = timeline.statements.filter(
    (statement) => statement.month <= month,
  )
  if (statements.length === 0) return []
  const shared = new Set(
    statementsSharingAMonth(timeline).map((group) => group[0].month),
  )
  const byMonth = new Map(
    statements
      .filter((statement) => !shared.has(statement.month))
      .map((statement) => [statement.month, statement]),
  )
  const missing = missingMonths(timeline, month)
  const pointMonths = Array.from(
    new Set([
      ...statements.map((statement) => statement.month),
      ...missing.filter((gap) => gap === month),
    ]),
  ).sort()
  const accountMonth = { accountId: timeline.id, accountName: timeline.name }
  return pointMonths.map((pointMonth, position) => {
    const previous = pointMonths[position - 1]
    const statement = byMonth.get(pointMonth)
    const asOfDate = statement?.endDate ?? lastDayOf(pointMonth)
    const hasGap = missing.includes(pointMonth)
    return {
      month: pointMonth,
      asOfDate,
      total: statement?.value ?? null,
      securities: statement?.securities ?? null,
      cash: statement?.cash ?? null,
      cashIncluded: statement?.cash != null,
      accountCount: 1,
      missing: hasGap ? [{ ...accountMonth, month: pointMonth }] : [],
      verifying: [],
      midCycle: [],
      unknownValue:
        statement && statement.value == null
          ? [
              {
                ...accountMonth,
                month: pointMonth,
                failedRead: isFailedRead(statement),
                readWithoutAccount: isReadWithoutAccount(
                  statement.reasons,
                  pointMonth,
                ),
              },
            ]
          : [],
      twoStatements: shared.has(pointMonth)
        ? [{ ...accountMonth, month: pointMonth }]
        : [],
      notStarted: [],
      hasGap,
      money: { window: { from: asOfDate, to: asOfDate }, ...NO_SERIES_MONEY },
      notCounted: notCountedOf(
        timeline,
        missing.filter(
          (gap) => gap <= pointMonth && (!previous || gap > previous),
        ),
      ),
    }
  })
}

// The account's months with no rows to count inside its money window, for the "Not counted:" line.
export const accountNotCounted = (
  timeline: AccountTimeline,
  month: MonthKey,
  window: DateWindow | null,
): NotCounted[] => {
  if (!window) return []
  const first = monthOf(window.from)
  const inWindow = (gap: MonthKey) => gap >= first
  return [
    ...notCountedOf(timeline, missingMonths(timeline, month).filter(inWindow)),
    ...notCountedOf(
      timeline,
      failedReadMonths(timeline, month).filter(inWindow),
      'failed_read',
    ),
  ]
}
